import { FieldModel, PracticeTemplateDetail, ProgramBenchmarkName, ProgramTask } from '@cibo/core'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Button, Divider, IconButton, Stack, TextField, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { uniq } from 'ramda'
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraitDataGrid } from '../TraitDataGrid'
import { TemplateEditorFooter } from './TemplateEditorFooter'
import { TemplateEditorTableCell } from './TemplateEditorTableCell'
import { TemplateEmptyState } from './TemplateEmptyState'

interface FieldTraitTemplateEditorMainProps {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
  draftDetails: PracticeTemplateDetail[]
  onValueSave: (
    traitId: string,
    year: number,
    filledValues: { value?: unknown; input?: unknown; result?: unknown }
  ) => void
  onValueDelete: (traitId: string, year: number) => void
  onAddPractice: (traitId: string) => void
  onPrePopulate: (fieldId: string) => void
  fields: FieldModel[]
  canEdit: boolean
  canChangeOwnerScope: boolean
  canDelete: boolean
  isDeleting: boolean
  isSaving: boolean
  isDirty: boolean
  draftName: string
  nameError: string | null
  selectedId: string | 'new'
  onDraftNameChange: (name: string) => void
  onConfirmName: () => boolean
  onDelete: () => void
  onClose: () => void
  onSave: () => void
  ownerScope: 'farmer' | 'all'
  onOwnerScopeChange: (scope: 'farmer' | 'all') => void
  availableTraitIds: string[]
  shouldFocusName?: boolean
  onShouldFocusNameHandled?: () => void
}

export const FieldTraitTemplateEditorMain = ({
  task,
  draftDetails,
  onValueSave,
  onValueDelete,
  onAddPractice,
  onPrePopulate,
  fields,
  canEdit,
  canChangeOwnerScope,
  canDelete,
  isDeleting,
  isSaving,
  isDirty,
  draftName,
  nameError,
  selectedId,
  onDraftNameChange,
  onConfirmName,
  onDelete,
  onClose,
  onSave,
  ownerScope,
  onOwnerScopeChange,
  availableTraitIds,
  shouldFocusName,
  onShouldFocusNameHandled,
}: FieldTraitTemplateEditorMainProps) => {
  const { t } = useTranslation(['traits', '@cibo/programs/TemplateEditorModal'])
  const [_forceRender, setForceRender] = useState(0)
  const [isNameEditing, setIsNameEditing] = useState(selectedId === 'new')
  const [pendingFocus, setPendingFocus] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setIsNameEditing(selectedId === 'new')
  }, [selectedId])

  useEffect(() => {
    if (shouldFocusName) {
      setIsNameEditing(true)
      setPendingFocus(true)
      onShouldFocusNameHandled?.()
    }
  }, [shouldFocusName, onShouldFocusNameHandled])

  useEffect(() => {
    if (pendingFocus && nameInputRef.current) {
      nameInputRef.current.focus()
      setPendingFocus(false)
    }
  }, [pendingFocus])

  const hasPractices = draftDetails.length > 0

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      setForceRender(prev => prev + 1)
    }, 50)
    return () => clearTimeout(timer)
  }, [hasPractices])

  const rows = useMemo(() => {
    const traitIds = uniq(draftDetails.map(d => d.traitId))
    return traitIds.map(traitId => {
      const d = draftDetails.find(detail => detail.traitId === traitId)
      const cellValue: Record<string, unknown> = {}
      if (d?.input !== undefined) cellValue.input = d.input
      if (d?.result !== undefined) cellValue.result = d.result
      if (d?.value !== undefined) cellValue.value = d.value
      return {
        id: traitId,
        traitId,
        traitValue: Object.keys(cellValue).length ? cellValue : undefined,
      }
    })
  }, [
    draftDetails
      .map(
        d =>
          `${d.traitId}-${d.year}-${JSON.stringify({
            input: d.input,
            result: d.result,
            value: d.value,
          })}`
      )
      .join('|'),
  ])

  const renderValueCell = useCallback(
    (params: GridRenderCellParams) => (
      <TemplateEditorTableCell
        task={task}
        canEdit={canEdit}
        onValueSave={onValueSave}
        onValueDelete={onValueDelete}
        {...params}
      />
    ),
    [task, canEdit, onValueSave, onValueDelete]
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'traitId',
        headerName: t('practice', { ns: '@cibo/programs/TemplateEditorModal' }),
        flex: 1.5,
        sortable: false,
        renderCell: ({ value }: GridRenderCellParams) => (
          <Stack direction="row" spacing={1} alignItems="center" width="100%">
            <Typography>{t(value, { ns: 'traits' })}</Typography>
          </Stack>
        ),
      },
      {
        field: 'traitValue',
        headerName: t('traitValue', { ns: '@cibo/programs/TemplateEditorModal' }),
        flex: 1,
        sortable: false,
        renderCell: renderValueCell,
      },
    ],
    [renderValueCell, t]
  )

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (onConfirmName()) setIsNameEditing(false)
    } else if (e.key === 'Escape' && selectedId !== 'new') {
      setIsNameEditing(false)
    }
  }

  const handleNameConfirm = () => {
    if (onConfirmName()) setIsNameEditing(false)
  }

  return (
    <Stack sx={{ flex: 1, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, minHeight: 48 }}>
        {isNameEditing ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: '50%', minWidth: 0 }}
          >
            <TextField
              autoFocus
              inputRef={nameInputRef}
              size="small"
              label={t('templateName', { ns: '@cibo/programs/TemplateEditorModal' })}
              value={draftName}
              onChange={e => onDraftNameChange(e.target.value)}
              onKeyDown={handleNameKeyDown}
              error={!!nameError}
              helperText={nameError ?? undefined}
              disabled={!canEdit}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              data-testid="template-name-input"
            />
            <Button
              size="small"
              onClick={handleNameConfirm}
              data-testid="template-name-confirm-button"
            >
              {t('save', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: '50%', minWidth: 0 }}
          >
            <Typography
              data-testid="template-name-display"
              sx={{
                fontSize: 16,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {draftName || t('newTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Typography>
            {canEdit && (
              <IconButton
                size="small"
                onClick={() => setIsNameEditing(true)}
                aria-label={t('editTemplateName', { ns: '@cibo/programs/TemplateEditorModal' })}
                data-testid="template-name-edit-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('close', { ns: '@cibo/programs/TemplateEditorModal' })}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {hasPractices ? (
          <TraitDataGrid rows={rows} columns={columns} hideFooter />
        ) : (
          <TemplateEmptyState
            fields={fields}
            canEdit={canEdit}
            hasPractices={hasPractices}
            onPrePopulate={onPrePopulate}
          />
        )}
      </Box>

      <TemplateEditorFooter
        canEdit={canEdit}
        canDelete={canDelete}
        canChangeOwnerScope={canChangeOwnerScope}
        isDeleting={isDeleting}
        hasPractices={hasPractices}
        isDirty={isDirty}
        draftName={draftName}
        isSaving={isSaving}
        selectedId={selectedId}
        onSave={onSave}
        onDelete={onDelete}
        ownerScope={ownerScope}
        onOwnerScopeChange={onOwnerScopeChange}
        availableTraitIds={availableTraitIds}
        onAddPractice={onAddPractice}
      />
    </Stack>
  )
}
